import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const LockContext = createContext(null);

const SESSIONS_KEY = 'restaflex_pin_sessions';
const SETTINGS_KEY = 'restaflex_lock_settings';
const LOCKED_KEY   = 'restaflex_screen_locked'; // sessionStorage — yenilemeye karşı

const DEFAULT_SETTINGS = { enabled: true, timeoutSeconds: 30 };

export function LockProvider({ children }) {
  const { user, loading } = useAuth();

  // Sayfa yenilenince kilit durumunu koru (sessionStorage)
  const [isLocked, setIsLockedState] = useState(() =>
    sessionStorage.getItem(LOCKED_KEY) === '1'
  );

  const setIsLocked = useCallback((val) => {
    if (val) sessionStorage.setItem(LOCKED_KEY, '1');
    else sessionStorage.removeItem(LOCKED_KEY);
    setIsLockedState(val);
  }, []);

  const [settings, setSettings] = useState(() => {
    try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') }; }
    catch { return DEFAULT_SETTINGS; }
  });

  const [pinSessions, setPinSessions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SESSIONS_KEY) || '[]'); }
    catch { return []; }
  });

  const timerRef = useRef(null);
  const isWaiter = user?.role === 'waiter';

  const resetTimer = useCallback(() => {
    if (!settings.enabled || !isWaiter) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsLocked(true), settings.timeoutSeconds * 1000);
  }, [settings.enabled, settings.timeoutSeconds, isWaiter, setIsLocked]);

  // Activity listeners — only for waiter role
  useEffect(() => {
    if (!settings.enabled || !isWaiter) return;
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [settings.enabled, isWaiter, resetTimer]);

  // Auth yüklendikten sonra waiter değilse kilidi kaldır
  // (loading sırasında user null gelir — o anda kilidi temizlememek için bekliyoruz)
  useEffect(() => {
    if (!loading && !isWaiter) setIsLocked(false);
  }, [loading, isWaiter, setIsLocked]);

  const lock = useCallback(() => {
    if (!isWaiter) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsLocked(true);
  }, [isWaiter, setIsLocked]);

  const unlock = useCallback((pin) => {
    // PIN kayıtlı değilse direkt aç
    if (pinSessions.length === 0) {
      setIsLocked(false);
      resetTimer();
      return true;
    }
    const session = pinSessions.find(s => s.pin === pin);
    if (!session) return false;

    if (session.userId !== user?.id) {
      // Farklı garsonun oturumuna geç
      localStorage.setItem('restaflex_token', session.token);
      sessionStorage.removeItem(LOCKED_KEY);
      window.location.reload();
    } else {
      setIsLocked(false);
      resetTimer();
    }
    return true;
  }, [pinSessions, user, resetTimer, setIsLocked]);

  const registerPin = useCallback((pin) => {
    if (!user) return;
    const token = localStorage.getItem('restaflex_token');
    const newSession = { pin, token, userId: user.id, userName: user.name, role: user.role };
    const updated = pinSessions.filter(s => s.userId !== user.id);
    updated.push(newSession);
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(updated));
    setPinSessions(updated);
  }, [user, pinSessions]);

  const removePin = useCallback((userId) => {
    const updated = pinSessions.filter(s => s.userId !== userId);
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(updated));
    setPinSessions(updated);
  }, [pinSessions]);

  // Tüm PIN oturumlarını sil (PIN unutma → çıkış akışı için)
  const clearAllPins = useCallback(() => {
    localStorage.removeItem(SESSIONS_KEY);
    setPinSessions([]);
  }, []);

  const updateSettings = useCallback((newSettings) => {
    const merged = { ...settings, ...newSettings };
    setSettings(merged);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(merged));
  }, [settings]);

  const currentUserPin = pinSessions.find(s => s.userId === user?.id)?.pin || null;

  return (
    <LockContext.Provider value={{
      isLocked, lock, unlock,
      registerPin, removePin, clearAllPins,
      settings, updateSettings,
      pinSessions, currentUserPin,
    }}>
      {children}
    </LockContext.Provider>
  );
}

export function useLock() {
  const ctx = useContext(LockContext);
  if (!ctx) throw new Error('useLock must be used within LockProvider');
  return ctx;
}
